import React, { useState } from 'react';
import { ApprovalWorkflowEditor, Button, Confirmation, Field, Form, HelpTooltip, Radio } from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useFeatures } from '~/hooks';
import SettingsFormSection from '../../SettingsFormSection';
import LabelTooltip from './LabelTooltip';

export default function ApprovalOptions({ project, formik }) {
  const api = useApi();
  const toast = useToast();
  const confirmation = useConfirmation();
  const features = useFeatures();
  const { workspace } = useWorkspace();

  const { client } = formik.values;

  const [timeApproval, setTimeApproval] = useState();

  const handleTimeApprovalEdit = async () => {
    const { data: timeApproval } = await api.www.workspaces(workspace.id).projects(project.id).timeApproval.get();
    setTimeApproval(timeApproval || {});
  };

  const handleTimeApprovalSubmit = async (value) => {
    const submit = async () => {
      try {
        await api.www.workspaces(workspace.id).projects(project.id).timeApproval.update(value);
        toast.success('The time approval workflow has been successfully updated.');
        setTimeApproval(null);
      } catch (error) {
        toast.error('An error has occurred updating the time approval workflow.');
      }
    };

    if (timeApproval.hasActiveWorkflows) {
      confirmation.prompt((resolve) => {
        const handleResolve = async (confirm) => {
          if (confirm) await submit();
          resolve();
        };

        return (
          <Confirmation resolve={handleResolve} title="Update Workflow">
            Are you sure that you want to update this approval workflow? This will restart the workflow for time entries
            on this project that are not in the Approved state.
          </Confirmation>
        );
      });
    } else {
      await submit();
    }
  };

  const [expenseApproval, setExpenseApproval] = useState();

  const handleExpenseApprovalEdit = async () => {
    const { data: expenseApproval } = await api.www.workspaces(workspace.id).projects(project.id).expenseApproval.get();
    setExpenseApproval(expenseApproval || {});
  };

  const handleExpenseApprovalSubmit = async (value) => {
    const submit = async () => {
      try {
        await api.www.workspaces(workspace.id).projects(project.id).expenseApproval.update(value);
        toast.success('The expense approval workflow has been successfully updated.');
        setExpenseApproval(null);
      } catch (error) {
        toast.error('An error has occurred updating the expense approval workflow.');
      }
    };

    if (expenseApproval.hasActiveWorkflows) {
      confirmation.prompt((resolve) => {
        const handleResolve = async (confirm) => {
          if (confirm) await submit();
          resolve();
        };

        return (
          <Confirmation resolve={handleResolve} title="Update Workflow">
            Are you sure that you want to update this approval workflow? This will restart the workflow for expense
            items on this project that are not in the Approved state.
          </Confirmation>
        );
      });
    } else {
      await submit();
    }
  };

  return (
    <SettingsFormSection title="Approval Options">
      <div>
        Time approval:
        <Form.Control style={{ alignItems: 'center', height: '2.5rem' }}>
          <Field.RadioGroup name="isTimeApprovalRequired">
            <Radio value={false} label="Automatic" />
            <Radio value={true} label="Manual" />
          </Field.RadioGroup>

          {features.approvalWorkflow && (
            <div style={{ flex: '.4' }}>
              <Button isOutline disabled={!formik.values.isTimeApprovalRequired} onClick={handleTimeApprovalEdit}>
                Edit Workflow
              </Button>
            </div>
          )}
        </Form.Control>
      </div>

      <div style={{ marginTop: '1rem' }}>
        Expense approval:
        <Form.Control style={{ alignItems: 'center', height: '2.5rem' }}>
          <Field.RadioGroup name="isExpensesApprovalRequired">
            <Radio value={false} label="Automatic" />
            <Radio value={true} label="Manual" />
          </Field.RadioGroup>

          {features.approvalWorkflow && (
            <div style={{ flex: '.4' }}>
              <Button
                isOutline
                disabled={!formik.values.isExpensesApprovalRequired}
                onClick={handleExpenseApprovalEdit}>
                Edit Workflow
              </Button>
            </div>
          )}
        </Form.Control>
      </div>

      {client?.isInternal === false && features.clientApprovals && (
        <Form.Control style={{ marginTop: '1rem' }}>
          <LabelTooltip>
            <Field.Checkbox label="Enable client approvals" name="enableClientApprovals" />
            <HelpTooltip
              message="Allows you to send time and expense approvals to the client."
              style={{ marginLeft: '0.5rem' }}
            />
          </LabelTooltip>
        </Form.Control>
      )}

      {timeApproval && (
        <ApprovalWorkflowEditor
          value={timeApproval}
          onSubmit={handleTimeApprovalSubmit}
          onClose={() => setTimeApproval(false)}
          onLoadWorkspaceWorkflow={api.www.workspaces(workspace.id).timeApproval.get}
        />
      )}

      {expenseApproval && (
        <ApprovalWorkflowEditor
          value={expenseApproval}
          onSubmit={handleExpenseApprovalSubmit}
          onClose={() => setExpenseApproval(false)}
          onLoadWorkspaceWorkflow={api.www.workspaces(workspace.id).expenseApproval.get}
        />
      )}
    </SettingsFormSection>
  );
}
